import * as React from "react";
import { Lock, Trash2 } from "lucide-react";

import { BookingWindow } from "@/api/types";
import { cn } from "@/lib/utils";

import { colorForDay } from "./windows";

type View = "cards" | "weeks";
/** i = index into the editable `windows` (removable); null = read-only, from an availability pattern. */
type Indexed = { w: BookingWindow; i: number | null };

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
const fmtDay = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

function mondayOf(iso: string): Date {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * The availability slots as a persistent canvas beside the calendar — never over it,
 * so the calendar stays the stable focus. Toggle between <b>Cards</b> (every slot) and
 * <b>Weeks</b> (a weekday × week grid of coloured circles showing the spread). Colours
 * come from {@link colorForDay}, matching the calendar blocks.
 *
 * <p>Slots from a chosen availability pattern ({@code typeWindows}) show here too — these are the
 * same slots the booking link offers — but read-only (a lock, not a bin): they're managed
 * by editing the availability pattern, so they aren't individually removable.</p>
 */
export function AvailabilityCanvas({
  windows,
  typeWindows = [],
  onRemove,
  onClearWeek,
  title = "Availability",
  emptyText = "No availability yet. Pick an availability pattern, drag on the calendar, or type a time to add a range.",
}: {
  windows: BookingWindow[];
  typeWindows?: BookingWindow[];
  onRemove: (index: number) => void;
  /** Clear every editable slot in one week (its Monday, ISO) — the per-row bin in Weeks view. */
  onClearWeek?: (weekStartIso: string) => void;
  /** Heading. A poll's rail holds proposed times, not availability, so it says so. */
  title?: string;
  /** Empty state. Defaults keep ad-hoc and rolling reading exactly as before. */
  emptyText?: string;
}) {
  const [view, setView] = React.useState<View>("cards");
  const items: Indexed[] = [
    ...windows.map((w, i) => ({ w, i })),
    ...typeWindows.map((w) => ({ w, i: null })),
  ];
  const total = windows.length + typeWindows.length;

  return (
    <aside
      className="flex w-full flex-col self-stretch overflow-hidden rounded-xl bg-muted/40"
      style={{
        backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1.3px)",
        backgroundSize: "14px 14px",
      }}
    >
      {/* flex-wrap so the Cards/Weeks toggle drops to its own line on the narrow gutter panel
          instead of being clipped by the aside's overflow-hidden. */}
      <header className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-3 py-2">
        <h3 className="flex min-w-0 items-center gap-2 text-xs font-bold uppercase tracking-wide text-foreground">
          {title}
          <span className="rounded-full bg-teal-100 px-2 text-xs font-bold text-teal-700 dark:bg-teal-950 dark:text-teal-300">
            {total}
          </span>
        </h3>
        <div className="inline-flex shrink-0 rounded-full border border-border bg-muted p-0.5 text-xs">
          {(["cards", "weeks"] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              aria-pressed={view === v}
              className={cn(
                "rounded-full px-2.5 py-1 font-semibold capitalize transition",
                view === v ? "bg-background text-foreground shadow-sm" : "text-muted-foreground",
              )}
            >
              <span>{v}</span>
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 overflow-auto p-3">
        {items.length === 0 ? (
          <p className="rounded-lg border-2 border-dashed border-black/20 p-4 text-center text-sm text-muted-foreground">
            {emptyText}
          </p>
        ) : view === "cards" ? (
          <CardsView items={items} onRemove={onRemove} />
        ) : (
          <WeeksView items={items} onRemove={onRemove} onClearWeek={onClearWeek} />
        )}
        {typeWindows.length > 0 && (
          <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-foreground">
            <Lock className="h-3.5 w-3.5 shrink-0" /> Faded ranges come from your availability pattern. Edit
            the type to change them.
          </p>
        )}
      </div>
    </aside>
  );
}

/** Every slot as a rounded pill with a coloured circle + a bin (or a lock, if from a type). */
function CardsView({ items, onRemove }: { items: Indexed[]; onRemove: (i: number) => void }) {
  const byDay = new Map<string, Indexed[]>();
  for (const it of [...items].sort((a, b) => a.w.start.localeCompare(b.w.start))) {
    const key = new Date(it.w.start).toDateString();
    const arr = byDay.get(key);
    if (arr) arr.push(it);
    else byDay.set(key, [it]);
  }
  return (
    <div className="space-y-3">
      {[...byDay.values()].map((group) => {
        const color = colorForDay(group[0].w.start);
        return (
          <div key={group[0].w.start}>
            <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
              {fmtDay(group[0].w.start)}
            </div>
            <div className="space-y-1.5">
              {group.map(({ w, i }, k) => (
                <div
                  key={i ?? `t-${k}`}
                  className="comic-shadow-sm flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-2"
                >
                  <span
                    className="h-6 w-6 shrink-0 rounded-full"
                    style={{ background: color, boxShadow: "inset 0 -3px 5px rgba(0,0,0,.18)" }}
                  />
                  <span className="text-[13px] font-semibold tabular-nums">
                    {fmtTime(w.start)} – {fmtTime(w.end)}
                  </span>
                  {i === null ? (
                    <span
                      title="From your availability pattern. Edit the type to change it"
                      className="ml-auto grid h-6 w-6 place-items-center text-muted-foreground/70"
                    >
                      <Lock className="h-3.5 w-3.5" />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onRemove(i)}
                      aria-label="Remove this range"
                      title="Remove this range"
                      className="ml-auto grid h-6 w-6 place-items-center rounded-full text-muted-foreground transition hover:bg-red-100 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A weekday × week grid of coloured circles — see the spread over many weeks. */
function WeeksView({
  items,
  onRemove,
  onClearWeek,
}: {
  items: Indexed[];
  onRemove: (i: number) => void;
  onClearWeek?: (weekStartIso: string) => void;
}) {
  const weeks = new Map<number, true>();
  for (const it of items) weeks.set(mondayOf(it.w.start).getTime(), true);
  const keys = [...weeks.keys()].sort((a, b) => a - b);
  const dows = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  return (
    <div className="overflow-x-auto">
      {/* minmax(0,1fr) lets the 7 day columns shrink to fit a narrow (gutter) panel, so
          Sunday never overflows/clips; a tighter label column + gap keep them legible. */}
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: "26px repeat(7, minmax(0, 1fr))" }}>
        <div />
        {dows.map((d, i) => (
          <div
            key={d}
            className={cn("pb-1 text-center text-[9px] font-bold", i >= 5 ? "text-muted-foreground/60" : "text-muted-foreground")}
          >
            {d}
          </div>
        ))}
        {keys.map((k) => {
          const mon = new Date(k);
          const weekEnd = new Date(mon);
          weekEnd.setDate(weekEnd.getDate() + 7);
          const editableThisWeek = items.some(
            (it) =>
              it.i !== null && new Date(it.w.start) >= mon && new Date(it.w.start) < weekEnd,
          );
          return (
            <React.Fragment key={k}>
              <div className="flex flex-col items-center gap-0.5 self-center text-[9px] tabular-nums text-muted-foreground">
                <span>{mon.toLocaleDateString(undefined, { day: "numeric", month: "short" })}</span>
                {editableThisWeek && onClearWeek && (
                  <button
                    type="button"
                    onClick={() => onClearWeek(mon.toISOString())}
                    title="Clear this week's ranges"
                    aria-label={`Clear this week's ranges (week of ${mon.toLocaleDateString()})`}
                    className="grid h-4 w-4 place-items-center rounded text-muted-foreground transition hover:bg-red-100 hover:text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                )}
              </div>
              {Array.from({ length: 7 }, (_, i) => {
                const day = new Date(mon);
                day.setDate(day.getDate() + i);
                const iso = isoDate(day);
                const here = items.filter((it) => isoDate(new Date(it.w.start)) === iso);
                return (
                  <div
                    key={i}
                    className={cn(
                      "flex min-h-[26px] flex-wrap content-center items-center justify-center gap-0.5 rounded-md p-0.5",
                      here.length ? "bg-foreground/[0.04]" : "",
                    )}
                  >
                    {here.map(({ w, i: idx }, k2) =>
                      idx === null ? (
                        <span
                          key={`t-${k2}`}
                          title={`${fmtDay(w.start)} · ${fmtTime(w.start)}–${fmtTime(w.end)}: from your availability pattern`}
                          className="h-3.5 w-3.5 rounded-full ring-1 ring-inset ring-black/20"
                          style={{ background: colorForDay(w.start), opacity: 0.55 }}
                        />
                      ) : (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => onRemove(idx)}
                          title={`${fmtDay(w.start)} · ${fmtTime(w.start)}–${fmtTime(w.end)}: click to remove this range`}
                          aria-label={`Availability range ${fmtTime(w.start)}–${fmtTime(w.end)}, click to remove`}
                          className="h-3.5 w-3.5 rounded-full transition hover:scale-125"
                          style={{ background: colorForDay(w.start), boxShadow: "inset 0 -2px 3px rgba(0,0,0,.22)" }}
                        />
                      ),
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
